import analytics from '@/shared-utils/analytics'

import {
  getLabelFromProductType,
  getRecentSelectionLabel,
  mapPackageTypeCode,
  getSelectionCurrency,
  getSelectionItemId,
  getSimilarHotelsLoadEcommerceCurrency,
  getSimilarHotelEcommerceCurrency,
  getSelectionProductName,
} from './ga4.helpers'
import type {
  AbandonedSelection,
  HotelDealMatch,
  PackageTypeCode,
  RecentTripSearches,
  SearchProductDataFiltered,
} from './types'

export function fireViewAllButtonClickEvent(pageName: 'homepage' | 'landing') {
  analytics.fireGA4Event({
    event: 'display',
    attributes: {
      type: 'link',
      item_name: 'view_all_recent_activity',
      product_name: 'hotel',
      page_name: pageName,
    },
  })

  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      product_name: 'hotel',
      link_name: 'view_all_recent_activity',
      page_name: pageName,
    },
  })
}

export function fireRecentSearchLoadEvent({
  searchItems,
  pageName,
}: {
  searchItems: readonly (SearchProductDataFiltered | RecentTripSearches)[]
  pageName: 'homepage' | 'landing'
}) {
  const ga4Items = searchItems.map((item) => ({
    item_name: getLabelFromProductType(item.productType),
    item_id: '',
  }))

  analytics.fireGA4Event({
    event: 'view_promotion',
    attributes: {
      type: 'recent_activity',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: 'search',
      page_name: pageName,
      ecommerce: {
        currency: '',
        items: ga4Items,
      },
    },
  })
}

export function fireRecentSearchCardClickEvent({
  productType,
  pageName,
}: {
  productType: 'STAY' | 'FLY' | 'DRIVE'
  pageName: 'homepage' | 'landing'
}) {
  analytics.fireGA4Event({
    event: 'select_promotion',
    attributes: {
      type: 'recent_activity',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: 'search',
      page_name: pageName,
      ecommerce: {
        currency: '',
        items: [
          {
            item_name: getLabelFromProductType(productType as string),
            item_id: '',
          },
        ],
      },
    },
  })
}

export function fireRecentSelectionLoad({
  selection,
  pageName,
  isTripGroupedSelection,
}: {
  selection: AbandonedSelection
  pageName: 'homepage' | 'landing'
  isTripGroupedSelection?: boolean
}) {
  analytics.fireGA4Event({
    event: 'view_promotion',
    attributes: {
      type: 'recent_activity',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: isTripGroupedSelection ? 'grouped_selection' : 'selection',
      page_name: pageName,
      ecommerce: {
        currency: getSelectionCurrency(selection),
        items: [
          {
            item_name: getRecentSelectionLabel(selection),
            item_id: getSelectionItemId(selection),
          },
        ],
      },
    },
  })
}

export function fireRecentSelectionClickEvent({
  selection,
  pageName,
  isTripGroupedSelection,
}: {
  selection: AbandonedSelection
  pageName: 'homepage' | 'landing'
  isTripGroupedSelection?: boolean
}) {
  analytics.fireGA4Event({
    event: 'select_promotion',
    attributes: {
      type: 'recent_activity',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: isTripGroupedSelection ? 'grouped_selection' : 'selection',
      page_name: pageName,
      product_name: getSelectionProductName(selection.productType),
      ecommerce: {
        currency: getSelectionCurrency(selection),
        items: [
          {
            item_name: getRecentSelectionLabel(selection),
            item_id: getSelectionItemId(selection),
          },
        ],
      },
    },
  })
}

export function fireFlightsOnContinueClickEvent() {
  analytics.fireGA4Event({
    event: 'select_promotion',
    attributes: {
      type: 'recent_activity',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: 'selection',
      page_name: 'landing',
      product_name: 'air',
      ecommerce: {
        currency: '',
        items: [{ item_name: 'air', item_id: '' }],
      },
    },
  })
}

export function fireOnBookABundleCheckboxClick(packageCode: PackageTypeCode) {
  const mappedPackageCode = mapPackageTypeCode(packageCode)

  analytics.fireGA4Event({
    event: 'select_promotion',
    attributes: {
      type: 'add_on',
      creative_name: 'pick_up_where_you_left_off',
      creative_slot: 'selection',
      page_name: 'landing',
      product_name: 'air',
      bundle_flag: 'y',
      bundle_type: mappedPackageCode,
    },
  })
}

export function fireFlightRecentSearchViewAllButtonClick() {
  analytics.fireGA4Event({
    event: 'display',
    attributes: {
      type: 'link',
      product_name: 'air',
      page_name: 'landing',
      item_name: 'view_all_recent_activity',
    },
  })
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      product_name: 'air',
      link_name: 'view_all_recent_activity',
      page_name: 'landing',
    },
  })
}

export function fireAddToWishlistToggleEvent({
  enabled,
  pageName,
}: {
  enabled: boolean
  pageName: 'homepage' | 'landing'
}) {
  analytics.fireGA4Event({
    event: 'add_to_wishlist',
    attributes: {
      page_name: pageName,
      product_name: 'hotel',
      enabled_flag: enabled ? 'y' : 'n',
      item_list_id: 'pick_up_where_you_left_off',
    },
  })
}

export function fireSimilarHotelsLoadEvent({
  displayedHotels,
  pageName,
}: {
  displayedHotels: readonly HotelDealMatch[]
  pageName: 'homepage' | 'landing'
}) {
  if (displayedHotels.length === 0) {
    return
  }
  analytics.fireGA4Event({
    event: 'view_promotion',
    attributes: {
      type: 'recent_activity' as const,
      page_name: pageName,
      creative_name: 'pick_up_where_you_left_off' as const,
      creative_slot: 'similar_hotels' as const,
      promotion_id: '',
      location_id: '',
      ecommerce: {
        currency: getSimilarHotelsLoadEcommerceCurrency(displayedHotels),
        items: displayedHotels.map((hotel, index) => {
          return {
            index: index + 1,
            item_name: 'hotel',
            item_category: null,
            item_id: String(hotel.hotelId),
          }
        }),
      },
    },
  })
}

export function fireSimilarHotelsClickEvent({
  hotel,
  index,
  pageName,
}: {
  hotel: HotelDealMatch
  index: number
  pageName: 'homepage' | 'landing'
}) {
  analytics.fireGA4Event({
    event: 'select_promotion',
    attributes: {
      type: 'recent_activity' as const,
      page_name: pageName,
      creative_name: 'pick_up_where_you_left_off' as const,
      creative_slot: 'similar_hotels' as const,
      promotion_id: '',
      location_id: '',
      ecommerce: {
        currency: getSimilarHotelEcommerceCurrency(hotel),
        items: [
          {
            index: index,
            item_name: 'hotel',
            item_category: null,
            item_id: String(hotel.hotelId),
          },
        ],
      },
    },
  })
}
